import { Avatar, Colors, Icon } from '@amelio/ui-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { buildSubmissionAnswersPayload } from './buildAnswerPayload';
import { EditCompetenciesModal } from './components/EditCompetenciesModal';
import { EmployeeInfoCard } from './components/EmployeeInfoCard';
import { EmployeeObjectivesSection } from './components/EmployeeObjectivesSection';
import { EvaluationDatePeriod } from './components/EvaluationDatePeriod';
import { MeetingTopics } from './components/MeetingTopics';
import { PerformanceHero } from './components/PerformanceHero';
import { PerformanceSurveySection } from './components/PerformanceSurveySection';
import { SelectedEmployeeArrow } from './components/SelectedEmployeeArrow';
import { SurveyCompletionScreen } from './components/SurveyCompletionScreen';
import { useDraftAnswerAutosave } from './hooks/useDraftAnswerAutosave';
import { useEvaluationPeriod } from './hooks/useEvaluationPeriod';
import { useManagerAssessmentInsights } from './hooks/useManagerAssessmentInsights';
import { useQuickAssessment } from './hooks/useQuickAssessment';
import { useSurveyAnswers } from './hooks/useSurveyAnswers';
import { getManagerAssessmentStepPath, isManagerAssessmentSubmitted } from './stepRoutes';
import { isManagerAssessmentSurveySubmitted } from './submissionState';
import { MeetingPreparationIllustration } from '../Results/components/MeetingPreparationIllustration';
import { AssessmentStepSkeleton } from '../shared/AssessmentStepSkeleton/AssessmentStepSkeleton';
import { StepHeader } from '../shared/StepHeader/StepHeader';

import type { EvaluationObjectivesPeriod } from './components/EmployeeObjectivesSection';
import type {
    EmployeeInfo,
    EvaluationLocationState,
    EvaluationPeriodOption,
    StepDefinition,
    SurveyQuestionData,
} from './types';
import type { EmployeeCompetency } from '@/types/manager-assessment/Competency';

import { useGetCompanyValues } from '@/api/company-values';
import { useGetProficiencyLevels } from '@/api/competency-services';
import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import {
    useGetEmployeeCompetencies,
    useGetEvaluationPeriods,
    useGetTemplateQuestions,
    useSaveManagerAssessment,
    useSaveManagerAssessmentAnswers,
    useSaveQuickAssessment,
} from '@/api/cycle-evaluation-services/manager-assessment';
import { useGetUserById } from '@/api/directory';
import { NotFoundState } from '@/components/NotFoundState/NotFoundState';
import { PlanLockProvider } from '@/components/ProGate/PlanLock';
import { PerformanceProFeature } from '@/constants/PerformanceProFeature';
import { Permission } from '@/constants/Permission';
import { QuestionType, type QuestionTypeValue } from '@/constants/QuestionType';
import { useAmelioTranslations } from '@/hooks';
import { useUserPermissions } from '@/hooks/api/useUserPermissions';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useAssessmentReadOnly } from '@/hooks/useAssessmentReadOnly';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { useError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import { showWarningToast } from '@/plugins/Toast';
import { toIsoDate } from '@/utils/dateUtils';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevelsWorstFirst } from '@/utils/proficiencyLevel';
import { getInitials, getProfilePictureUrl } from '@/utils/user';

/** `'YYYY-MM-DD'`: the calendar-day part of a period's dates. */
const ISO_DATE_LENGTH = 10;

export const ManagerAssessmentEvaluationPage = () => {
    const { t } = useAmelioTranslations();
    const { getJobTitle } = useCompanyInformation();
    const replaceDynamicTags = useReplaceDynamicTags();
    const { handleError } = useError();
    const location = useLocation<EvaluationLocationState | undefined>();
    const navigate = useNavigate();
    const { mutateAsync: saveAnswers, isPending: isSubmittingAnswers } =
        useSaveManagerAssessmentAnswers();
    const { mutateAsync: saveQuickAssessment } = useSaveQuickAssessment();
    const { mutateAsync: saveManagerAssessment, isPending: isSavingCompetencies } =
        useSaveManagerAssessment();

    const [isEditCompetenciesOpen, setIsEditCompetenciesOpen] = useState(false);

    const state = location.state;
    // The id lives in the URL so a refresh keeps the context; location.state is only a hint.
    const { cycleInstanceId: cycleInstanceIdParam } = useParams<{ cycleInstanceId?: string }>();
    const cycleInstanceId = cycleInstanceIdParam ?? state?.cycleInstanceId;

    // Questions selected for the manager assessment are snapshotted onto the CycleInstance.
    const {
        data: cycleInstance,
        isLoading: isCycleInstanceLoading,
        isError: hasCycleInstanceFailed,
    } = useGetCycleInstance(cycleInstanceId ?? '');
    const templateId = cycleInstance?.reviewQuestionnaire?.reviewTemplateId ?? state?.templateId;

    // Derived from the CycleInstance so the page rehydrates on refresh (no location.state):
    // the assessed employee is the reviewee.
    const employeeId =
        cycleInstance?.reviewAudience?.reviewAudienceAssignments?.[0]?.assignedTo ||
        state?.employeeId;

    const { data: employeeUser } = useGetUserById({ userId: employeeId ?? '' });
    const { data: evaluationPeriodsRaw = [] } = useGetEvaluationPeriods();
    const { data: templateQuestions = [] } = useGetTemplateQuestions(templateId);
    // The assessed employee's profile competencies — the pool for the survey/grid and the Edit
    // Competencies modal (we scope to the profile, not the whole company library).
    const { data: employeeCompetencies = [] } = useGetEmployeeCompetencies(employeeId);
    const { data: proficiencyLevels = [] } = useGetProficiencyLevels();
    // #13699: the quick assessment shortcut is gated on its own capability permission. Read at
    // page load, so a permission granted mid-session surfaces on the next load, not live.
    const { can } = useUserPermissions();
    const canUseQuickAssessment = can(Permission.UseQuickAssessment);
    const canIncludeEngagement = can(Permission.IncludeEngagementResultsInPerformanceInsights);

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [isSurveyCompleted, setIsSurveyCompleted] = useState(false);
    const [isQuickMode, setIsQuickMode] = useState(false);

    // Read-only mode for completed assessments (prevents historical record corruption)
    const { isReadOnly, isLockedByPlan } = useAssessmentReadOnly(cycleInstance);
    // Once submitted, the server refuses any change to the competencies, company values or template, since it
    // would delete submitted answers (#16638). Answers stay editable.
    const isSetupLocked =
        isManagerAssessmentSurveySubmitted(cycleInstance) ||
        isManagerAssessmentSubmitted(cycleInstance?.managerAssessmentStep);
    const hasReconciledOnMountRef = useRef(false);

    useEffect(() => {
        if (hasReconciledOnMountRef.current) {
            return;
        }
        if (!cycleInstanceId || !employeeId || !cycleInstance || isReadOnly || isSetupLocked) {
            return;
        }

        const competencyIdsFromCycle =
            cycleInstance.assessmentCompetencies?.map((entry) => entry.competencyId) ?? [];
        const includeCompanyValuesFlag = cycleInstance.includeCompanyValues ?? false;

        if (competencyIdsFromCycle.length === 0 && !includeCompanyValuesFlag) {
            return;
        }

        hasReconciledOnMountRef.current = true;

        void saveManagerAssessment({
            cycleInstanceId,
            payload: {
                assesseeId: employeeId,
                competencies: competencyIdsFromCycle.map((competencyId) => ({ competencyId })),
                includeCompanyValues: includeCompanyValuesFlag,
                managerTemplateId: templateId,
            },
        }).catch((error) => handleError({ error }));
    }, [
        cycleInstanceId,
        cycleInstance,
        employeeId,
        isReadOnly,
        isSetupLocked,
        saveManagerAssessment,
        templateId,
        handleError,
    ]);

    const {
        selectedPeriod,
        evaluationDate,
        customFromDate,
        customToDate,
        isPeriodInherited,
        isCustomPeriodInvalid,
        handleEvaluationDateChange,
        handlePeriodChange,
        handleCustomFromChange,
        handleCustomToChange,
    } = useEvaluationPeriod(cycleInstanceId, cycleInstance, evaluationPeriodsRaw, isReadOnly);

    const employee = useMemo<EmployeeInfo | null>(() => {
        if (!employeeUser) return null;
        return {
            firstName: employeeUser.firstName,
            lastName: employeeUser.lastName,
            roleTitle: employeeUser.jobTitleId ? getJobTitle(employeeUser.jobTitleId) : '',
            avatarUrl: getProfilePictureUrl(employeeUser.profilePictureUrl) || '',
        };
    }, [employeeUser, getJobTitle]);

    const evaluationPeriods = useMemo<EvaluationPeriodOption[]>(
        () =>
            evaluationPeriodsRaw.map((period) => ({
                value: period.id,
                label: period.name,
                from: period.startDate,
                to: period.endDate,
            })),
        [evaluationPeriodsRaw],
    );

    const employeeFirstName = employee?.firstName ?? '';

    // A note is linked to a question of this assessment. When the assessment has no questions of
    // its own yet and the survey falls back on its template's, there is no question to link a note
    // to, so no note is offered rather than one the server would refuse.
    const takesQuestionNotes =
        (cycleInstance?.reviewQuestionnaire?.reviewQuestions?.length ?? 0) > 0;

    // The objectives are read in the period the manager has set above the survey. A predefined
    // period carries its own calendar dates, taken as they are: turned into a `Date`, a
    // `'YYYY-MM-DD'` reads as UTC midnight and lands on the previous day west of Greenwich. A custom
    // range is the pair of days the manager picked. An unset, half-set or inverted range has no
    // window to read them in.
    const predefinedPeriod = evaluationPeriods.find((period) => period.value === selectedPeriod);
    const objectivesPeriod = ((): EvaluationObjectivesPeriod | null => {
        if (predefinedPeriod) {
            return {
                from: predefinedPeriod.from.slice(0, ISO_DATE_LENGTH),
                to: predefinedPeriod.to.slice(0, ISO_DATE_LENGTH),
                name: predefinedPeriod.label,
            };
        }
        if (selectedPeriod && customFromDate && customToDate && !isCustomPeriodInvalid) {
            return { from: toIsoDate(customFromDate), to: toIsoDate(customToDate) };
        }
        return null;
    })();

    const surveyQuestions = useMemo<SurveyQuestionData[]>(() => {
        const fallbackRatingOptions = [
            t('managerAssessmentEvaluation.survey.ratingOptions.exceeds'),
            t('managerAssessmentEvaluation.survey.ratingOptions.meets'),
            t('managerAssessmentEvaluation.survey.ratingOptions.needsImprovement'),
            t('managerAssessmentEvaluation.survey.ratingOptions.below'),
        ];
        // Manager assessment: render the competency questions snapshotted onto the CycleInstance.
        // Section titles are kept so the shared Answer component renders them as headers.
        const reviewQuestions = cycleInstance?.reviewQuestionnaire?.reviewQuestions ?? [];
        if (reviewQuestions.length > 0) {
            return reviewQuestions.map((question) => {
                const questionOptions = question.reviewQuestionOptions ?? [];
                const isPlainText = question.questionType === QuestionType.PLAIN_TEXT;
                return {
                    competencyId: question.id ?? '',
                    question: replaceDynamicTags({
                        text: getLocalizedText(question.label),
                        employee,
                        competencyName: question.competencyName,
                        companyValueName: question.companyValueName,
                    }),
                    // Plain-text and Yes/No questions have no options
                    options:
                        isPlainText || question.questionType === QuestionType.YES_NO
                            ? []
                            : questionOptions.length
                              ? questionOptions.map((option) => getLocalizedText(option.label))
                              : fallbackRatingOptions,
                    optionIds: questionOptions.map((option) => option.id ?? ''),
                    insights: question.usefulTip ? getLocalizedText(question.usefulTip) : '',
                    questionType: question.questionType,
                    reviewQuestion: question,
                };
            });
        }

        if (templateQuestions.length > 0) {
            return templateQuestions.map((tq) => ({
                competencyId: tq.id,
                question: replaceDynamicTags({
                    text: getLocalizedText(tq.text ?? tq.label),
                    employee,
                }),
                options:
                    tq.questionType === QuestionType.PLAIN_TEXT ||
                    tq.questionType === QuestionType.YES_NO
                        ? []
                        : fallbackRatingOptions,
                optionIds: [],
                insights: tq.usefulTip ? getLocalizedText(tq.usefulTip) : '',
                questionType: tq.questionType,
                // Template questions carry no persisted options — synthesize the minimal
                // ReviewQuestion shape the Answer component needs.
                reviewQuestion: {
                    id: tq.id,
                    label: { en: getLocalizedText(tq.text ?? tq.label) },
                    questionType: tq.questionType as QuestionTypeValue,
                    reviewQuestionOptions: [],
                },
            }));
        }
        return [];
    }, [cycleInstance, templateQuestions, t, replaceDynamicTags, employee]);

    const {
        answerValues,
        otherTextValues,
        loadedAnswers,
        isDirty: isAnswersDirty,
        handleAnswerChange,
        handleOtherTextChange,
        markSaved: markAnswersSaved,
    } = useSurveyAnswers(cycleInstance, surveyQuestions);

    const hasSubmittedSurvey = isManagerAssessmentSurveySubmitted(cycleInstance);

    // Answers are persisted question by question as the manager moves through the survey, so a refresh
    // mid-survey doesn't lose them. This is the draft endpoint — submitting below is a separate call.
    const { saveCurrentAnswer: saveDraftAnswer } = useDraftAnswerAutosave({
        cycleInstanceId,
        isReadOnly,
        // Past submission the results are already computed from these answers, so a revision only counts
        // once it goes through submit again — the draft endpoint rejects writes at that point too.
        isSubmitted: hasSubmittedSurvey,
        questions: surveyQuestions,
        answerState: { answerValues, otherTextValues },
        loadedAnswers,
        currentQuestionId: surveyQuestions[currentQuestionIndex]?.competencyId,
        // The quick assessment hides the survey: its current question was never shown, so leaving the page from
        // there must not write it as a skipped answer (#17414).
        isQuickAssessment: isQuickMode,
    });

    const {
        assessments,
        isDirty: isAssessmentsDirty,
        handleAssessmentChange,
        setAssessments,
        markSaved: markAssessmentsSaved,
    } = useQuickAssessment(cycleInstance, proficiencyLevels);

    // The grid rates only the competencies snapshotted on this evaluation.
    const selectedCompetencyIds = useMemo(
        () =>
            cycleInstance?.assessmentCompetencies?.map((entry) => entry.competencyId) ??
            state?.competencyIds ??
            [],
        [cycleInstance, state],
    );

    const evaluationCompetencies = useMemo(() => {
        const selectedIdSet = new Set(selectedCompetencyIds);
        return employeeCompetencies.filter((competency) => selectedIdSet.has(competency.id));
    }, [selectedCompetencyIds, employeeCompetencies]);

    // The Edit Competencies modal picks from the assessed employee's profile competencies (not the
    // company-wide library), so the manager can only include competencies from that employee's profile.
    const editCompetenciesPool = employeeCompetencies;

    // Company values are rated in the quick assessment too (when included). Model each as a
    // competency-like item under a "Company Values" group so the grid renders/rates them uniformly.
    const { data: companyValues = [] } = useGetCompanyValues();

    const companyValueItems = useMemo<EmployeeCompetency[]>(() => {
        const assessed = cycleInstance?.assessmentCompanyValues ?? [];
        if (assessed.length === 0) {
            return [];
        }
        const nameById = new Map(
            companyValues.map((value) => [value.id, getLocalizedText(value.name)]),
        );
        const groupName = t('managerAssessmentEvaluation.quickAssessment.companyValuesGroup');
        return assessed.map((value) => ({
            id: value.companyValueId,
            name: nameById.get(value.companyValueId) ?? '',
            description: null,
            groupName,
            lastAssessedAt: null,
            lastAssessedProficiencyLevelId: null,
            questionCount: 0,
            isDeactivated: false,
        }));
    }, [cycleInstance, companyValues, t]);

    // Ids that belong to company values (vs competencies) — used to split the quick-assessment save.
    const companyValueIdSet = useMemo(
        () => new Set(companyValueItems.map((item) => item.id)),
        [companyValueItems],
    );

    // Everything rated in the quick assessment grid: competencies followed by company values.
    const quickAssessmentItems = useMemo(
        () => [...evaluationCompetencies, ...companyValueItems],
        [evaluationCompetencies, companyValueItems],
    );

    // Persist an edited competency selection (and company-values toggle) back to the CycleInstance;
    // the setup save regenerates the questionnaire, so the survey/quick-assessment refresh on refetch.
    const handleSaveCompetencies = useCallback(
        async ({
            competencyIds,
            includeCompanyValues,
        }: {
            competencyIds: string[];
            includeCompanyValues: boolean;
        }) => {
            if (!cycleInstanceId || !employeeId) {
                return;
            }

            try {
                await saveManagerAssessment({
                    cycleInstanceId,
                    payload: {
                        assesseeId: employeeId,
                        competencies: competencyIds.map((competencyId) => ({ competencyId })),
                        includeCompanyValues,
                        managerTemplateId: templateId,
                    },
                });

                // Drop cached ratings for items no longer selected. The backend soft-deletes a
                // removed competency's answer, so a re-added competency must start unrated rather
                // than resurfacing its previous (now-stale) proficiency level from local state.
                setAssessments((prev) => {
                    const allowedIds = new Set(competencyIds);
                    if (includeCompanyValues) {
                        companyValueIdSet.forEach((id) => allowedIds.add(id));
                    }
                    return Object.fromEntries(
                        Object.entries(prev).filter(([id]) => allowedIds.has(id)),
                    );
                });

                setIsEditCompetenciesOpen(false);
            } catch (error) {
                handleError({ error });
            }
        },
        [
            cycleInstanceId,
            employeeId,
            templateId,
            saveManagerAssessment,
            handleError,
            setAssessments,
            companyValueIdSet,
        ],
    );

    const questionIds = useMemo(
        () => surveyQuestions.map((surveyQuestion) => surveyQuestion.competencyId),
        [surveyQuestions],
    );

    // AI insights (engagement / 360 / self) per question, generated on demand and streamed back.
    const competencyNameById = useMemo(() => {
        const map: Record<string, string> = {};
        for (const competency of employeeCompetencies) {
            map[competency.id] = competency.name;
        }
        return map;
    }, [employeeCompetencies]);

    const insightQuestions = useMemo(
        () =>
            surveyQuestions.map((surveyQuestion) => ({
                id: surveyQuestion.competencyId,
                text: surveyQuestion.question,
                competency:
                    getLocalizedText(surveyQuestion?.reviewQuestion?.competencyName ?? undefined) ||
                    competencyNameById[surveyQuestion.reviewQuestion?.competencyId ?? ''] ||
                    '',
            })),
        [surveyQuestions, competencyNameById],
    );
    const {
        insightByQuestion,
        errorByQuestion,
        isGenerating,
        canGenerate,
        generate,
        retryQuestion,
    } = useManagerAssessmentInsights(
        cycleInstanceId,
        insightQuestions,
        cycleInstance?.evaluationPeriodStart,
        cycleInstance?.evaluationPeriodEnd,
    );

    // Meeting topics persisted on the CycleInstance (memoized so the MeetingTopics sync effect
    // only fires when the data actually changes).
    const initialMeetingTopics = useMemo(
        () =>
            [...(cycleInstance?.meetingTopics ?? [])]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((topic) => ({
                    id: topic.id,
                    subject: topic.subject,
                    notes: topic.notes ?? '',
                })),
        [cycleInstance],
    );

    const currentQuestion = surveyQuestions[currentQuestionIndex];
    // The index one past the last question is the pre-submit completion screen.
    const isAtCompletionScreen =
        surveyQuestions.length > 0 && currentQuestionIndex >= surveyQuestions.length;
    const hasNextQuestion = currentQuestionIndex < surveyQuestions.length;
    const hasPreviousQuestion = currentQuestionIndex > 0;

    // Wrappers for Answer component - curry the questionId
    const handleCurrentAnswerChange = useCallback(
        (values: string[]) => {
            if (currentQuestion) {
                handleAnswerChange(currentQuestion.competencyId, values);
            }
        },
        [currentQuestion, handleAnswerChange],
    );

    const handleCurrentOtherTextChange = useCallback(
        (value: string) => {
            if (currentQuestion) {
                handleOtherTextChange(currentQuestion.competencyId, value);
            }
        },
        [currentQuestion, handleOtherTextChange],
    );

    // Leaving the survey for the quick assessment persists an answer edited on the current question, as moving off
    // it would; an untouched question is not sent.
    const handleToggleMode = useCallback(() => {
        if (!isQuickMode) {
            saveDraftAnswer();
        }
        setIsQuickMode((prev) => !prev);
    }, [isQuickMode, saveDraftAnswer]);

    const handleRateCompetency = useCallback(
        ({ competencyId, levelIndex }: { competencyId: string; levelIndex: number }) => {
            handleAssessmentChange(competencyId, levelIndex);
        },
        [handleAssessmentChange],
    );

    // Moving off a question is what persists it — the answer is never saved while it's being edited.
    const handleNextQuestion = useCallback(() => {
        if (hasNextQuestion) {
            saveDraftAnswer();
            setCurrentQuestionIndex((prev) => prev + 1);
        }
    }, [hasNextQuestion, saveDraftAnswer]);

    const handlePreviousQuestion = useCallback(() => {
        if (hasPreviousQuestion) {
            saveDraftAnswer();
            setCurrentQuestionIndex((prev) => prev - 1);
        }
    }, [hasPreviousQuestion, saveDraftAnswer]);

    const handleDotClick = useCallback(
        (index: number) => {
            saveDraftAnswer();
            setCurrentQuestionIndex(index);
        },
        [saveDraftAnswer],
    );

    // The progress dots move freely between questions, so a mandatory answer is enforced here, once,
    // rather than on every way of leaving a question.
    const firstUnansweredMandatoryIndex = surveyQuestions.findIndex(
        (surveyQuestion) =>
            surveyQuestion.reviewQuestion.isAnswerMandatory &&
            surveyQuestion.questionType !== QuestionType.SECTION_TITLE &&
            !answerValues[surveyQuestion.competencyId]?.length,
    );

    const handleSubmitSurvey = useCallback(async () => {
        // A read-only assessment is never submitted, whatever the screen offers.
        if (isReadOnly) {
            return;
        }
        if (firstUnansweredMandatoryIndex >= 0) {
            setCurrentQuestionIndex(firstUnansweredMandatoryIndex);
            showWarningToast({
                message: t('managerAssessmentEvaluation.survey.mandatoryUnanswered', {
                    number: firstUnansweredMandatoryIndex + 1,
                }),
            });
            return;
        }
        // Nothing edited since load/last save — skip the API call and just advance. Only safe once the
        // survey has actually been submitted before: autosaved drafts hydrate as "not dirty" too, and
        // they never ran the submission side effects (score, stepper, proficiency history).
        if (!isAnswersDirty && hasSubmittedSurvey) {
            setIsSurveyCompleted(true);
            return;
        }
        if (cycleInstanceId) {
            const answers = buildSubmissionAnswersPayload(surveyQuestions, {
                answerValues,
                otherTextValues,
            });

            try {
                await saveAnswers({ cycleInstanceId, payload: { answers } });
                // Reset dirty before advancing so a re-submit with no new edits skips the API.
                markAnswersSaved();
                // Only advance to the completion screen once the save actually succeeds.
                setIsSurveyCompleted(true);
            } catch (error) {
                handleError({ error });
            }
        }
    }, [
        isReadOnly,
        firstUnansweredMandatoryIndex,
        t,
        cycleInstanceId,
        surveyQuestions,
        answerValues,
        otherTextValues,
        saveAnswers,
        handleError,
        isAnswersDirty,
        hasSubmittedSurvey,
        markAnswersSaved,
    ]);

    // Quick assessment: persist the proficiency level chosen for each competency. The grid stores
    // a levelIndex into the same worst→best order it renders, so resolve it to the level's id.
    const handleSubmitQuickAssessment = useCallback(async () => {
        // Nothing changed since load/last save — skip the API call and just advance.
        if (!isAssessmentsDirty) {
            setIsSurveyCompleted(true);
            return;
        }
        if (isReadOnly) {
            return;
        }
        if (!cycleInstanceId) {
            return;
        }
        const orderedLevels = orderProficiencyLevelsWorstFirst(proficiencyLevels);
        const assessmentsPayload: { competencyId: string; proficiencyLevelId: string | null }[] =
            [];
        const companyValuesPayload: {
            companyValueId: string;
            proficiencyLevelId: string | null;
        }[] = [];
        Object.entries(assessments).forEach(([id, levelIndex]) => {
            const proficiencyLevelId = orderedLevels[levelIndex]?.id ?? null;
            if (companyValueIdSet.has(id)) {
                companyValuesPayload.push({ companyValueId: id, proficiencyLevelId });
            } else {
                assessmentsPayload.push({ competencyId: id, proficiencyLevelId });
            }
        });

        try {
            await saveQuickAssessment({
                cycleInstanceId,
                payload: { assessments: assessmentsPayload, companyValues: companyValuesPayload },
            });
            // Reset dirty before advancing so a re-submit with no new edits skips the API.
            markAssessmentsSaved();
            setIsSurveyCompleted(true);
        } catch (error) {
            handleError({ error });
        }
    }, [
        isReadOnly,
        cycleInstanceId,
        assessments,
        companyValueIdSet,
        proficiencyLevels,
        saveQuickAssessment,
        handleError,
        isAssessmentsDirty,
        markAssessmentsSaved,
    ]);

    const handleBackToLastQuestion = useCallback(() => {
        setCurrentQuestionIndex(surveyQuestions.length - 1);
    }, [surveyQuestions.length]);

    const handleViewResults = useCallback(() => {
        navigate({
            path: `${RouteName.ManagerAssessmentResults}/${cycleInstanceId ?? ''}`,
            state: {
                ...state,
                assessments,
            },
        });
    }, [navigate, state, assessments, cycleInstanceId]);

    const handleBackToAssessment = useCallback(() => {
        setIsSurveyCompleted(false);
        setCurrentQuestionIndex(0);
    }, []);

    const handleStepClick = useCallback(
        (step: StepDefinition) => {
            navigate({
                path: getManagerAssessmentStepPath(step.id, cycleInstanceId),
                state: {
                    ...state,
                    assessments,
                },
            });
        },
        [navigate, state, assessments, cycleInstanceId],
    );

    const handlePrepareMeetingClick = useCallback(() => {
        navigate({
            path: `${RouteName.ManagerAssessmentPrepareMeeting}/${cycleInstanceId ?? ''}`,
            state,
        });
    }, [navigate, state, cycleInstanceId]);

    useEffect(() => {
        if (!cycleInstanceId && !state?.employeeId) {
            navigate({ path: RouteName.ManagerAssessment, replace: true });
        }
    }, [cycleInstanceId, state, navigate]);

    // The assessed employee is read from the cycle instance, so until that answer arrives the page
    // knows nothing. Rendering the same blank for "still asking" and for "there is no such cycle"
    // is what makes an address carrying the wrong kind of id look like a broken screen.
    if (!cycleInstanceId && !state?.employeeId) {
        return null; // The effect above is already sending this render to the setup page.
    }

    if (isCycleInstanceLoading) {
        return (
            <div data-testid={'manager-assessment-evaluation-page'}>
                <StepHeader
                    currentStep={1}
                    onStepClick={handleStepClick}
                />

                <AssessmentStepSkeleton dataTestId={'manager-assessment-evaluation-skeleton'} />
            </div>
        );
    }

    if (hasCycleInstanceFailed || !employeeId) {
        return (
            <NotFoundState
                titleKey={'errors.notFoundOrInaccessibleTitle'}
                messageKey={'errors.notFoundOrInaccessibleMessage'}
            />
        );
    }

    return (
        <PlanLockProvider
            isLocked={isLockedByPlan}
            feature={PerformanceProFeature.ManagerAssessment}
        >
            <div data-testid={'manager-assessment-evaluation-page'}>
                <StepHeader
                    currentStep={1}
                    employeeFirstName={employeeFirstName}
                    onStepClick={handleStepClick}
                />

                <main
                    className={
                        'assessment-journey flex flex-col gap-12 pb-16 md:gap-20 md:pb-24 lg:gap-32'
                    }
                >
                    {/* Top Section */}
                    <div className={'evaluation-header-offset relative'}>
                        {/* Mobile: Unified Hero Section */}
                        {/* hide-from-md (not md:hidden): Tailwind v4 responsive variants don't override
                        base utilities in this project — see src/responsive-utilities.css. */}
                        <div
                            className={
                                'hide-from-md rounded-2xl border border-white bg-white/45 p-4 backdrop-blur-[2px]'
                            }
                        >
                            <div className={'mb-[16px] flex items-start gap-3'}>
                                <div
                                    className={
                                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-100'
                                    }
                                >
                                    <Icon
                                        name={'arrow-refresh-2'}
                                        size={20}
                                        color={Colors.PRIMARY}
                                    />
                                </div>

                                <div className={'min-w-0 flex-1'}>
                                    <h3
                                        className={
                                            'text-[18px] leading-6 font-medium text-gray-900'
                                        }
                                    >
                                        {t('managerAssessmentEvaluation.prepareAssessment')}
                                    </h3>

                                    <p
                                        className={
                                            'mt-1 text-[12px] leading-relaxed text-gray-900/70'
                                        }
                                    >
                                        {t('managerAssessmentEvaluation.prepareAssessmentDesc')}
                                    </p>
                                </div>
                            </div>

                            <div
                                className={
                                    'flex items-center gap-3 rounded-xl border border-[rgba(16,207,189,0.3)] bg-white/70 p-3'
                                }
                            >
                                <Avatar
                                    src={getProfilePictureUrl(employee?.avatarUrl) || undefined}
                                    alt={`${employee?.firstName} ${employee?.lastName}`}
                                    initials={employee ? getInitials(employee) : ''}
                                    size={'sm'}
                                    bgColor={'purple'}
                                />

                                <div className={'min-w-0 flex-1'}>
                                    <span
                                        className={
                                            'block truncate text-[14px] font-medium text-gray-900'
                                        }
                                    >
                                        {`${employee?.firstName} ${employee?.lastName}`}
                                    </span>

                                    <span className={'block truncate text-[12px] text-gray-500'}>
                                        {employee?.roleTitle}
                                    </span>
                                </div>

                                <span
                                    className={
                                        'bg-primary shrink-0 rounded-full px-2 py-1 text-[10px] text-white'
                                    }
                                >
                                    {t('managerAssessmentEvaluation.employeeCard.selected')}
                                </span>
                            </div>
                        </div>

                        {/* Desktop: Separate cards */}
                        {/* show-from-md + col-to-row-xl (not hidden md:flex lg:flex-row): the raw
                        responsive variants don't apply here — see src/responsive-utilities.css. */}
                        <div className={'show-from-md col-to-row-md items-start gap-10 lg:!gap-30'}>
                            <PerformanceHero />

                            <div
                                className={'relative md:mt-[37px]'}
                                data-testid={'selected-employee-column'}
                            >
                                {employee && <EmployeeInfoCard employee={employee} />}

                                <SelectedEmployeeArrow />
                            </div>
                        </div>
                    </div>

                    {/* Performance Section: Date, Period, Info, Survey */}
                    <div className={'evaluation-header-gap flex flex-col gap-5 md:gap-7'}>
                        <EvaluationDatePeriod
                            evaluationDate={evaluationDate}
                            onDateChange={handleEvaluationDateChange}
                            selectedPeriod={selectedPeriod}
                            periods={evaluationPeriods}
                            onPeriodChange={handlePeriodChange}
                            fromDate={customFromDate}
                            toDate={customToDate}
                            onFromDateChange={handleCustomFromChange}
                            onToDateChange={handleCustomToChange}
                            isPeriodInherited={isPeriodInherited}
                            disabled={isReadOnly}
                            customRange={{ enforcesChronology: true }}
                            errorMessage={
                                isCustomPeriodInvalid
                                    ? t('managerAssessmentEvaluation.customPeriodInvalid')
                                    : undefined
                            }
                        />

                        {/* <InfoBanner employeeFirstName={employeeFirstName} /> */}

                        {isReadOnly && (
                            <div
                                className={
                                    'mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900'
                                }
                            >
                                <p className={'font-medium'}>
                                    {t(
                                        isLockedByPlan
                                            ? 'managerAssessmentEvaluation.readOnly.lockedByPlanMessage'
                                            : 'managerAssessmentEvaluation.readOnly.message',
                                    )}
                                </p>
                            </div>
                        )}

                        <div className={'animate-in fade-in slide-in-from-bottom-4 duration-500'}>
                            {isSurveyCompleted && (
                                <SurveyCompletionScreen
                                    variant={'submitted'}
                                    onViewResults={handleViewResults}
                                    onBackToAssessment={handleBackToAssessment}
                                />
                            )}

                            {!isSurveyCompleted && (
                                <PerformanceSurveySection
                                    currentQuestion={currentQuestion}
                                    onNext={handleNextQuestion}
                                    onPrevious={handlePreviousQuestion}
                                    selectedValues={
                                        answerValues[currentQuestion?.competencyId ?? ''] ?? []
                                    }
                                    onAnswerChange={handleCurrentAnswerChange}
                                    hasNext={hasNextQuestion}
                                    hasPrevious={hasPreviousQuestion}
                                    assessments={assessments}
                                    answerValues={answerValues}
                                    reviewee={employeeUser}
                                    currentQuestionIndex={currentQuestionIndex}
                                    totalQuestions={surveyQuestions.length}
                                    onDotClick={handleDotClick}
                                    questionIds={questionIds}
                                    insight={insightByQuestion[currentQuestion?.competencyId ?? '']}
                                    insightError={
                                        errorByQuestion[currentQuestion?.competencyId ?? '']
                                    }
                                    isInsightLoading={
                                        isGenerating &&
                                        !insightByQuestion[currentQuestion?.competencyId ?? ''] &&
                                        !errorByQuestion[currentQuestion?.competencyId ?? '']
                                    }
                                    onGenerateInsights={generate}
                                    onRetryInsights={
                                        currentQuestion
                                            ? () => retryQuestion(currentQuestion.competencyId)
                                            : undefined
                                    }
                                    isGeneratingInsights={isGenerating}
                                    canGenerateInsights={canGenerate}
                                    insightsDisabledHint={t(
                                        'managerAssessmentEvaluation.insights.periodRequired',
                                    )}
                                    employeeId={employeeId}
                                    cycleInstanceId={cycleInstanceId}
                                    takesQuestionNotes={takesQuestionNotes}
                                    otherText={
                                        otherTextValues[currentQuestion?.competencyId ?? ''] ?? ''
                                    }
                                    onOtherTextChange={handleCurrentOtherTextChange}
                                    isQuickMode={isQuickMode}
                                    onToggleMode={handleToggleMode}
                                    canUseQuickAssessment={canUseQuickAssessment}
                                    canIncludeEngagement={canIncludeEngagement}
                                    onEditCompetencies={() => setIsEditCompetenciesOpen(true)}
                                    canEditCompetencies={!isSetupLocked}
                                    competencies={quickAssessmentItems}
                                    proficiencyLevels={proficiencyLevels}
                                    onRate={handleRateCompetency}
                                    onSubmit={handleSubmitQuickAssessment}
                                    readOnly={isReadOnly}
                                    // Past the last question the section keeps its card, title and actions,
                                    // and swaps the question out for the submit screen.
                                    completion={
                                        !isQuickMode && isAtCompletionScreen ? (
                                            <SurveyCompletionScreen
                                                variant={'ready'}
                                                employeeFirstName={employeeFirstName}
                                                alreadySubmitted={
                                                    hasSubmittedSurvey && !isAnswersDirty
                                                }
                                                isSubmitting={isSubmittingAnswers}
                                                onSubmit={handleSubmitSurvey}
                                                onPreviousQuestion={handleBackToLastQuestion}
                                            />
                                        ) : undefined
                                    }
                                />
                            )}
                        </div>
                    </div>

                    {employeeId && employee && (
                        <EmployeeObjectivesSection
                            employeeId={employeeId}
                            employeeFirstName={employee.firstName}
                            period={objectivesPeriod}
                        />
                    )}

                    <MeetingTopics
                        employeeFirstName={employeeFirstName}
                        cycleInstanceId={cycleInstanceId ?? ''}
                        initialTopics={initialMeetingTopics}
                        disabled={isReadOnly}
                    />

                    <div className={'md:my-8 md:w-5/6 md:py-10'}>
                        <MeetingPreparationIllustration
                            employeeFirstName={employeeFirstName}
                            onPrepareMeetingClick={handlePrepareMeetingClick}
                        />
                    </div>
                </main>

                <EditCompetenciesModal
                    isOpen={isEditCompetenciesOpen}
                    competencies={editCompetenciesPool}
                    companyValues={companyValues}
                    initialSelectedIds={selectedCompetencyIds}
                    initialIncludeCompanyValues={cycleInstance?.includeCompanyValues ?? false}
                    isSaving={isSavingCompetencies}
                    onClose={() => setIsEditCompetenciesOpen(false)}
                    onSave={handleSaveCompetencies}
                />
            </div>
        </PlanLockProvider>
    );
};
